import { getGestureLabel } from '../config/gestureMotions';
import { getDanceLabel } from '../config/danceMotions';
import {
  CURVE_WALKING_MOTION_IDS, getWalkingLabel, normalizeWalkingData, SIDE_STEP_WALKING_MOTION_IDS,
} from '../config/walkingMotions';
import { getCustomMotion } from '../config/customMotions';
import { JOINT_VID_MAP } from '../animation/wegoJoints';
import {
  DEFAULT_LIGHT_COLOR,
  getLightPayload,
  isLightColor,
  LIGHT_EXECUTION_DURATION_MS,
} from '../config/lightPresets';
import { DEFAULT_SOUND_EFFECT, isSoundEffect } from '../config/soundEffects';
import { COIN_FLIP_SOURCE_HANDLES } from '../nodes/coinFlipHandles';
import { COUNTER_SOURCE_HANDLES, COUNTER_TARGET_HANDLES } from '../nodes/counterHandles';
import { IF_SOURCE_HANDLES } from '../nodes/ifHandles';
import { KEYWORD_SWITCH_SOURCE_HANDLES } from '../nodes/keywordSwitchHandles';
import { MAZE_GOAL_SOURCE_HANDLES } from '../nodes/mazeGoalHandles';
import { SWITCH_SOURCE_HANDLES } from '../nodes/switchHandles';
import type { AppEdge, AppNode, NodeKind } from '../types';

const INDENT = '  ';

const NODE_NAME_PREFIX: Record<NodeKind, string> = {
  start: 'start',
  end: 'end',
  input: 'input',
  output: 'output',
  function: 'function',
  textEdit: 'textEdit',
  sayText: 'sayText',
  gesture: 'gesture',
  dance: 'dance',
  qrScan: 'qrScan',
  tof: 'tof',
  listen: 'listen',
  if: 'if',
  switch: 'switch',
  counter: 'counter',
  coinFlip: 'coinFlip',
  wait: 'wait',
  randomChoice: 'randomChoice',
  keywordSwitch: 'keywordSwitch',
  soundEffect: 'soundEffect',
  light: 'light',
  customGesture: 'customGesture',
  walking: 'walking',
  mazeGoal: 'mazeGoal',
};

const SOURCE_HANDLE_ORDER: Partial<Record<NodeKind, readonly string[]>> = {
  if: [IF_SOURCE_HANDLES.true, IF_SOURCE_HANDLES.false],
  switch: [
    SWITCH_SOURCE_HANDLES.match1,
    SWITCH_SOURCE_HANDLES.match2,
    SWITCH_SOURCE_HANDLES.default,
  ],
  counter: [
    COUNTER_SOURCE_HANDLES.inRange,
    COUNTER_SOURCE_HANDLES.done,
  ],
  coinFlip: [
    COIN_FLIP_SOURCE_HANDLES.heads,
    COIN_FLIP_SOURCE_HANDLES.tails,
  ],
  keywordSwitch: [
    KEYWORD_SWITCH_SOURCE_HANDLES.match1,
    KEYWORD_SWITCH_SOURCE_HANDLES.match2,
    KEYWORD_SWITCH_SOURCE_HANDLES.default,
  ],
};

const DEFAULT_WAIT_SECONDS = 1;
const DEFAULT_RANDOM_CHOICES = ['A', 'B', 'C'] as const;
const MAX_WAIT_SECONDS = 60;
const MAX_CHOICE_LENGTH = 200;

const isValidWaitSeconds = (seconds: number): boolean => (
  Number.isFinite(seconds)
  && seconds >= 0
  && seconds <= MAX_WAIT_SECONDS
  && Math.abs(seconds * 10 - Math.round(seconds * 10)) < 1e-8
);

const isValidRandomChoice = (choice: unknown): choice is string => (
  typeof choice === 'string'
  && choice.trim() !== ''
  && [...choice].length <= MAX_CHOICE_LENGTH
);

const escapeString = (value: string): string => value
  .replace(/\\/g, '\\\\')
  .replace(/'/g, "\\'")
  .replace(/\r/g, '\\r')
  .replace(/\n/g, '\\n')
  .replace(/\t/g, '\\t')
  .replace(/\u2028/g, '\\u2028')
  .replace(/\u2029/g, '\\u2029');

const stringLiteral = (value: string): string => `'${escapeString(value)}'`;

const numberLiteral = (value: number): string => {
  if (!Number.isFinite(value)) return 'NaN';
  return Object.is(value, -0) ? '0' : String(value);
};

const scaleLiteral = (value: number): string => {
  const literal = numberLiteral(value);
  return Number.isInteger(value) && Number.isFinite(value) ? `${literal}.0` : literal;
};

const compareNodePosition = (left: AppNode | undefined, right: AppNode | undefined): number => {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  const yDifference = left.position.y - right.position.y;
  if (yDifference !== 0) return yDifference;
  const xDifference = left.position.x - right.position.x;
  if (xDifference !== 0) return xDifference;
  return left.id.localeCompare(right.id);
};

const indentLines = (lines: string[], level: number): string[] => {
  const prefix = INDENT.repeat(level);
  return lines.map((line) => line ? `${prefix}${line}` : '');
};

type HelperDefinition = {
  name: string;
  rootId: string;
  kind: 'branch' | 'join';
  path: Set<string>;
  stopAtCounterRepeat?: string;
};

type RenderOptions = {
  path: Set<string>;
  allowJoinRoot?: boolean;
  stopAtCounterRepeat?: string;
};

/**
 * 現在のフローを、説明表示用の JavaScript 風ソースへ変換する。
 * 生成結果は実行用ではなく、フローの構造と設定値を読み取るための派生情報である。
 */
export function generateFlowSource(nodes: AppNode[], edges: AppEdge[]): string {
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const edgeIndexes = new Map(edges.map((edge, index) => [edge, index]));
  const outMap = new Map<string, AppEdge[]>();
  const inMap = new Map<string, AppEdge[]>();

  for (const edge of edges) {
    const outgoing = outMap.get(edge.source) ?? [];
    outgoing.push(edge);
    outMap.set(edge.source, outgoing);
    const incoming = inMap.get(edge.target) ?? [];
    incoming.push(edge);
    inMap.set(edge.target, incoming);
  }

  const handleOrder = (node: AppNode, handle: string | null | undefined): number => {
    const order = SOURCE_HANDLE_ORDER[node.type] ?? [];
    const index = handle == null ? -1 : order.indexOf(handle);
    return index < 0 ? order.length : index;
  };

  const sortedOutgoing = (nodeId: string): AppEdge[] => {
    const source = nodeMap.get(nodeId);
    return [...(outMap.get(nodeId) ?? [])].sort((left, right) => {
      if (source) {
        const handleDifference = handleOrder(source, left.sourceHandle) - handleOrder(source, right.sourceHandle);
        if (handleDifference !== 0) return handleDifference;
      }
      const positionDifference = compareNodePosition(nodeMap.get(left.target), nodeMap.get(right.target));
      if (positionDifference !== 0) return positionDifference;
      return (edgeIndexes.get(left) ?? 0) - (edgeIndexes.get(right) ?? 0);
    });
  };

  const startNodes = nodes
    .filter((node) => node.type === 'start' || node.type === 'input')
    .sort(compareNodePosition);
  const primaryStart = startNodes[0];
  const entryLabel = primaryStart?.type === 'input' ? 'input' : 'Start';
  const reachable = new Set<string>();
  const traversalOrder: AppNode[] = [];

  const visit = (nodeId: string) => {
    if (reachable.has(nodeId)) return;
    const node = nodeMap.get(nodeId);
    if (!node) return;
    reachable.add(nodeId);
    traversalOrder.push(node);
    for (const edge of sortedOutgoing(nodeId)) visit(edge.target);
  };

  if (primaryStart) visit(primaryStart.id);
  const remainingNodes = nodes
    .filter((node) => !reachable.has(node.id))
    .sort(compareNodePosition);
  const namingOrder = [...traversalOrder, ...remainingNodes];
  const counters = new Map<NodeKind, number>();
  const displayNames = new Map<string, string>();
  for (const node of namingOrder) {
    const next = (counters.get(node.type) ?? 0) + 1;
    counters.set(node.type, next);
    displayNames.set(node.id, `${NODE_NAME_PREFIX[node.type]}${next}`);
  }

  const displayName = (nodeId: string): string => displayNames.get(nodeId) ?? 'unknownCard';
  const branchEdges = (node: AppNode, sourceHandle: string): AppEdge[] => {
    const outgoing = sortedOutgoing(node.id);
    const matched = outgoing.filter((edge) => edge.sourceHandle === sourceHandle);
    return matched.length > 0
      ? matched
      : outgoing.filter((edge) => edge.sourceHandle == null);
  };

  const hasCounterRepeatPath = (counterId: string, candidates: AppEdge[]): boolean => {
    const seen = new Set<string>();
    const inspect = (edge: AppEdge): boolean => {
      if (edge.target === counterId && edge.targetHandle === COUNTER_TARGET_HANDLES.repeat) return true;
      if (edge.target === counterId || seen.has(edge.target)) return false;
      seen.add(edge.target);
      return sortedOutgoing(edge.target).some(inspect);
    };
    return candidates.some(inspect);
  };

  let branchNumber = 0;
  const joinHelpers = new Map<string, string>();
  const functionHelpers = new Map<string, string[]>();
  let collectingUnconnectedHelpers = false;
  const reachableHelpers: HelperDefinition[] = [];
  const unconnectedHelpers: HelperDefinition[] = [];

  const addHelper = (helper: HelperDefinition) => {
    if (collectingUnconnectedHelpers) unconnectedHelpers.push(helper);
    else reachableHelpers.push(helper);
  };

  const scheduleBranch = (rootId: string, options: RenderOptions): string => {
    branchNumber += 1;
    const name = `runBranch${branchNumber}`;
    addHelper({
      name,
      rootId,
      kind: 'branch',
      path: new Set(options.path),
      stopAtCounterRepeat: options.stopAtCounterRepeat,
    });
    return name;
  };

  const scheduleJoin = (rootId: string, options: RenderOptions): string => {
    const existing = joinHelpers.get(rootId);
    if (existing) return existing;
    const cardName = displayName(rootId);
    const name = `continueFrom${cardName[0].toUpperCase()}${cardName.slice(1)}`;
    joinHelpers.set(rootId, name);
    addHelper({
      name,
      rootId,
      kind: 'join',
      path: new Set(options.path),
      stopAtCounterRepeat: options.stopAtCounterRepeat,
    });
    return name;
  };

  const scheduleFunction = (node: Extract<AppNode, { type: 'function' }>): string => {
    const existing = functionHelpers.get(node.id);
    const name = `run${displayName(node.id)[0].toUpperCase()}${displayName(node.id).slice(1)}`;
    if (existing) return name;

    if (node.data.internalNodes.length === 0) {
      functionHelpers.set(node.id, [
        `async function ${name}(context) {`,
        `${INDENT}// Flow warning: Function ${node.data.functionName || 'Unnamed'} is empty.`,
        '}',
      ]);
      return name;
    }

    const nestedSource = generateFlowSource(node.data.internalNodes, node.data.internalEdges);
    const nestedLines = nestedSource.trimEnd().split('\n');
    const mainStart = nestedLines.indexOf('async function main() {');
    const mainEnd = nestedLines.findIndex((line, index) => index > mainStart && line === '}');
    const body = mainStart >= 0 && mainEnd > mainStart
      ? nestedLines.slice(mainStart + 1, mainEnd).filter((line) => line !== `${INDENT}const context = {};`)
      : [`${INDENT}// Flow warning: Function source could not be generated.`];
    const nestedHelpers = mainEnd >= 0 ? nestedLines.slice(mainEnd + 1).filter((line) => line !== '') : [];
    const lines = [
      `// Function: ${node.data.functionName || 'Unnamed'}`,
      `async function ${name}(parentContext) {`,
      `${INDENT}const context = { ...parentContext, outputReached: false };`,
    ];
    if (nestedHelpers.length > 0) {
      lines.push('', ...nestedHelpers.map((line) => `${INDENT}${line}`));
    }
    lines.push(
      `${INDENT}try {`,
      ...body.map((line) => `${INDENT}${line}`),
      `${INDENT}} finally {`,
      `${INDENT.repeat(2)}parentContext.value = context.outputReached ? context.value : undefined;`,
      `${INDENT}}`,
      '}',
    );
    functionHelpers.set(node.id, lines);
    return name;
  };

  const warningForMissingTarget = (edge: AppEdge): string =>
    `// Flow warning: ${displayName(edge.source)} points to a card that no longer exists.`;

  const renderTargets = (
    candidates: AppEdge[],
    level: number,
    options: RenderOptions,
  ): string[] => {
    if (candidates.length === 0) {
      return indentLines(['// Flow warning: this branch is not connected to another card.'], level);
    }

    const activeEdges = options.stopAtCounterRepeat
      ? candidates.filter((edge) => !(
          edge.target === options.stopAtCounterRepeat
          && edge.targetHandle === COUNTER_TARGET_HANDLES.repeat
        ))
      : candidates;
    const stoppedAtRepeat = activeEdges.length !== candidates.length;
    if (activeEdges.length === 0) return [];

    if (activeEdges.length === 1) {
      const edge = activeEdges[0];
      if (!nodeMap.has(edge.target)) return indentLines([warningForMissingTarget(edge)], level);
      return renderFrom(edge.target, level, { ...options, path: new Set(options.path) });
    }

    const lines = indentLines(['await Promise.all(['], level);
    for (const edge of activeEdges) {
      if (!nodeMap.has(edge.target)) {
        lines.push(...indentLines([warningForMissingTarget(edge)], level + 1));
        continue;
      }
      const helperName = scheduleBranch(edge.target, options);
      lines.push(`${INDENT.repeat(level + 1)}${helperName}(cloneContext(context)),`);
    }
    lines.push(`${INDENT.repeat(level)}]);`);
    if (stoppedAtRepeat) {
      lines.push(...indentLines(['// The other branch returns to the Counter repeat input.'], level));
    }
    return lines;
  };

  const renderSwitch = (node: Extract<AppNode, { type: 'switch' }>, level: number, options: RenderOptions): string[] => {
    const lines: string[] = [];
    const conditions = [
      { prefix: `if (context.value === ${stringLiteral(node.data.matchValue1)}) {`, handle: SWITCH_SOURCE_HANDLES.match1 },
      { prefix: `else if (context.value === ${stringLiteral(node.data.matchValue2)}) {`, handle: SWITCH_SOURCE_HANDLES.match2 },
      { prefix: 'else {', handle: SWITCH_SOURCE_HANDLES.default },
    ];

    conditions.forEach((condition, index) => {
      if (index > 0) lines.push('');
      lines.push(`${INDENT.repeat(level)}${condition.prefix}`);
      lines.push(...renderTargets(branchEdges(node, condition.handle), level + 1, {
        ...options,
        path: new Set(options.path),
      }));
      lines.push(`${INDENT.repeat(level)}}`);
    });
    return lines;
  };

  const renderIf = (
    node: Extract<AppNode, { type: 'if' }>,
    level: number,
    options: RenderOptions,
  ): string[] => {
    const compareValue = stringLiteral(node.data.compareValue);
    const inputNumber = "(typeof context.value === 'number' && Number.isFinite(context.value) ? context.value : (typeof context.value === 'string' && context.value.trim() !== '' && Number.isFinite(Number(context.value)) ? Number(context.value) : null))";
    const compareNumber = Number(node.data.compareValue);
    const hasNumericCompareValue = node.data.compareValue.trim() !== '' && Number.isFinite(compareNumber);
    let condition: string;

    if (node.data.operator === '=' || node.data.operator === '≠') {
      const equality = hasNumericCompareValue
        ? `(${inputNumber} !== null ? ${inputNumber} === ${numberLiteral(compareNumber)} : String(context.value ?? '') === ${compareValue})`
        : `String(context.value ?? '') === ${compareValue}`;
      condition = node.data.operator === '=' ? equality : `!${equality}`;
    } else if (!hasNumericCompareValue) {
      condition = 'false';
    } else {
      const operator = node.data.operator === '=>' ? '>=' : node.data.operator;
      condition = `${inputNumber} !== null && ${inputNumber} ${operator} ${numberLiteral(compareNumber)}`;
    }

    const lines = [`${INDENT.repeat(level)}if (${condition}) {`];
    lines.push(...renderTargets(branchEdges(node, IF_SOURCE_HANDLES.true), level + 1, {
      ...options,
      path: new Set(options.path),
    }));
    lines.push(`${INDENT.repeat(level)}} else {`);
    lines.push(...renderTargets(branchEdges(node, IF_SOURCE_HANDLES.false), level + 1, {
      ...options,
      path: new Set(options.path),
    }));
    lines.push(`${INDENT.repeat(level)}}`);
    return lines;
  };

  const renderCoinFlip = (
    node: Extract<AppNode, { type: 'coinFlip' }>,
    level: number,
    options: RenderOptions,
  ): string[] => {
    const lines = [`${INDENT.repeat(level)}if (Math.random() < 0.5) {`];
    lines.push(...renderTargets(branchEdges(node, COIN_FLIP_SOURCE_HANDLES.heads), level + 1, {
      ...options,
      path: new Set(options.path),
    }));
    lines.push(`${INDENT.repeat(level)}} else {`);
    lines.push(...renderTargets(branchEdges(node, COIN_FLIP_SOURCE_HANDLES.tails), level + 1, {
      ...options,
      path: new Set(options.path),
    }));
    lines.push(`${INDENT.repeat(level)}}`);
    return lines;
  };

  const renderMazeGoal = (
    node: Extract<AppNode, { type: 'mazeGoal' }>,
    level: number,
    options: RenderOptions,
  ): string[] => {
    const lines = [`${INDENT.repeat(level)}if (Boolean(context.isAtGoal?.())) {`];
    lines.push(...renderTargets(branchEdges(node, MAZE_GOAL_SOURCE_HANDLES.true), level + 1, {
      ...options,
      path: new Set(options.path),
    }));
    lines.push(`${INDENT.repeat(level)}} else {`);
    lines.push(...renderTargets(branchEdges(node, MAZE_GOAL_SOURCE_HANDLES.false), level + 1, {
      ...options,
      path: new Set(options.path),
    }));
    lines.push(`${INDENT.repeat(level)}}`);
    return lines;
  };

  const renderKeywordSwitch = (
    node: Extract<AppNode, { type: 'keywordSwitch' }>,
    level: number,
    options: RenderOptions,
  ): string[] => {
    const keyword1 = typeof node.data.keyword1 === 'string' ? node.data.keyword1 : '';
    const keyword2 = typeof node.data.keyword2 === 'string' ? node.data.keyword2 : '';
    const input = "String(context.value ?? '')";
    const conditions = [
      {
        prefix: `if (${stringLiteral(keyword1)} !== '' && ${input}.includes(${stringLiteral(keyword1)})) {`,
        handle: KEYWORD_SWITCH_SOURCE_HANDLES.match1,
      },
      {
        prefix: `else if (${stringLiteral(keyword2)} !== '' && ${input}.includes(${stringLiteral(keyword2)})) {`,
        handle: KEYWORD_SWITCH_SOURCE_HANDLES.match2,
      },
      { prefix: 'else {', handle: KEYWORD_SWITCH_SOURCE_HANDLES.default },
    ];
    const lines: string[] = [];

    conditions.forEach((condition, index) => {
      if (index > 0) lines.push('');
      lines.push(`${INDENT.repeat(level)}${condition.prefix}`);
      lines.push(...renderTargets(branchEdges(node, condition.handle), level + 1, {
        ...options,
        path: new Set(options.path),
      }));
      lines.push(`${INDENT.repeat(level)}}`);
    });
    return lines;
  };

  const renderCounter = (node: Extract<AppNode, { type: 'counter' }>, level: number, options: RenderOptions): string[] => {
    const { initialValue, stepValue, finalValue } = node.data;
    const inRangeEdges = branchEdges(node, COUNTER_SOURCE_HANDLES.inRange);
    const doneEdges = branchEdges(node, COUNTER_SOURCE_HANDLES.done);
    const comparison = stepValue < 0 ? '>=' : '<=';
    const counterName = displayName(node.id);
    const lines: string[] = [];

    if (!Number.isFinite(initialValue) || !Number.isFinite(stepValue) || !Number.isFinite(finalValue)) {
      return indentLines([
        `// Flow warning: ${counterName} has an invalid numeric setting.`,
        'return;',
      ], level);
    }

    if (Math.abs(stepValue) < Number.EPSILON) {
      return indentLines([
        `// Flow warning: ${counterName} has a zero step and cannot advance.`,
        `let count = ${numberLiteral(initialValue)};`,
        'context.value = count;',
      ], level);
    }

    if (hasCounterRepeatPath(node.id, inRangeEdges)) {
      lines.push(`${INDENT.repeat(level)}for (let count = ${numberLiteral(initialValue)}; count ${comparison} ${numberLiteral(finalValue)}; count += ${numberLiteral(stepValue)}) {`);
      lines.push(`${INDENT.repeat(level + 1)}context.value = count;`);
      lines.push(...renderTargets(inRangeEdges, level + 1, {
        ...options,
        path: new Set(options.path),
        stopAtCounterRepeat: node.id,
      }));
      lines.push(`${INDENT.repeat(level)}}`);
      lines.push('');
      lines.push(...renderTargets(doneEdges, level, {
        ...options,
        path: new Set(options.path),
      }));
      return lines;
    }

    lines.push(`${INDENT.repeat(level)}let count = ${numberLiteral(initialValue)};`);
    lines.push(`${INDENT.repeat(level)}// Flow warning: ${counterName} does not loop back to its repeat input,`);
    lines.push(`${INDENT.repeat(level)}// so this block runs at most once.`);
    lines.push(`${INDENT.repeat(level)}if (count ${comparison} ${numberLiteral(finalValue)}) {`);
    lines.push(`${INDENT.repeat(level + 1)}context.value = count;`);
    lines.push(...renderTargets(inRangeEdges, level + 1, {
      ...options,
      path: new Set(options.path),
    }));
    lines.push(`${INDENT.repeat(level)}} else {`);
    lines.push(...renderTargets(doneEdges, level + 1, {
      ...options,
      path: new Set(options.path),
    }));
    lines.push(`${INDENT.repeat(level)}}`);
    return lines;
  };

  function renderFrom(nodeId: string, level: number, options: RenderOptions): string[] {
    const node = nodeMap.get(nodeId);
    if (!node) return indentLines(['// Flow warning: a connected card no longer exists.'], level);

    if (options.path.has(nodeId)) {
      return indentLines([
        `// Flow warning: unsupported loop detected at ${displayName(nodeId)}.`,
      ], level);
    }

    if (!options.allowJoinRoot && (inMap.get(nodeId)?.length ?? 0) > 1) {
      return indentLines([`await ${scheduleJoin(nodeId, options)}(context);`], level);
    }

    const path = new Set(options.path).add(nodeId);
    const nextOptions: RenderOptions = {
      ...options,
      path,
      allowJoinRoot: false,
    };
    const lines: string[] = [];

    switch (node.type) {
      case 'start':
      case 'input':
        break;
      case 'end':
        return indentLines(['return;'], level);
      case 'output':
        return indentLines(['context.outputReached = true;'], level);
      case 'function':
        lines.push(`${INDENT.repeat(level)}await ${scheduleFunction(node)}(context);`);
        break;
      case 'textEdit':
        lines.push(`${INDENT.repeat(level)}context.value = ${stringLiteral(node.data.text)};`);
        lines.push(`${INDENT.repeat(level)}await wait(500);`);
        break;
      case 'sayText':
        lines.push(`${INDENT.repeat(level)}await robots.say(context.value, {`);
        lines.push(`${INDENT.repeat(level + 1)}speaker: ${numberLiteral(node.data.speaker)},`);
        lines.push(`${INDENT.repeat(level + 1)}speedScale: ${scaleLiteral(node.data.speedScale)},`);
        lines.push(`${INDENT.repeat(level + 1)}pitchScale: ${scaleLiteral(node.data.pitchScale)},`);
        lines.push(`${INDENT.repeat(level)}});`);
        break;
      case 'gesture':
        lines.push(`${INDENT.repeat(level)}await robots.gesture({ motionId: ${stringLiteral(node.data.motionId)}, name: ${stringLiteral(getGestureLabel(node.data.motionId) ?? '未設定')} });`);
        break;
      case 'walking': {
        const walking = normalizeWalkingData(node.data);
        if (walking.stepCount !== undefined && SIDE_STEP_WALKING_MOTION_IDS.has(walking.motionId ?? '')) {
          const left = walking.motionId === '1002';
          lines.push(`${INDENT.repeat(level)}// Native side step (robot motion ${walking.motionId} with a repetition argument).`);
          lines.push(`${INDENT.repeat(level)}await robots.${left ? 'moveLeft' : 'moveRight'}({ times: ${numberLiteral(walking.stepCount)} });`);
        } else if (walking.stepCount !== undefined && CURVE_WALKING_MOTION_IDS.has(walking.motionId ?? '')) {
          const right = walking.motionId === '1009';
          lines.push(`${INDENT.repeat(level)}// Native curve walk (robot motion ${walking.motionId} with a step argument, ${right ? 'left' : 'right'} foot first); the closing motion is not counted as a step.`);
          lines.push(`${INDENT.repeat(level)}await robots.${right ? 'curveRight' : 'curveLeft'}({ steps: ${numberLiteral(walking.stepCount)} });`);
        } else if (walking.stepCount !== undefined) {
          const backward = walking.motionId === '5';
          lines.push(`${INDENT.repeat(level)}// Native left-first gait (robot motion ${backward ? 1001 : 1000} with a step argument); the closing motion is not counted as a step.`);
          lines.push(`${INDENT.repeat(level)}await robots.${backward ? 'walkBackward' : 'walkForward'}({ steps: ${numberLiteral(walking.stepCount)} });`);
        } else {
          lines.push(`${INDENT.repeat(level)}await robots.gesture({ motionId: ${stringLiteral(walking.motionId)}, name: ${stringLiteral(getWalkingLabel(walking.motionId) ?? '未設定')} });`);
        }
        break;
      }
      case 'customGesture': {
        const motion = getCustomMotion(node.data.motionId);
        if (!motion || motion.poses.length === 0) {
          lines.push(`${INDENT.repeat(level)}// Flow warning: ${displayName(node.id)} has no gesture selected.`);
          break;
        }
        lines.push(`${INDENT.repeat(level)}await robots.create({`);
        lines.push(`${INDENT.repeat(level + 1)}id: ${stringLiteral(motion.id)},`);
        lines.push(`${INDENT.repeat(level + 1)}name: ${stringLiteral(motion.label)},`);
        lines.push(`${INDENT.repeat(level + 1)}poses: [`);
        motion.poses.forEach((pose) => {
          const entries = Object.entries(pose.joints)
            .filter(([jointId]) => jointId in JOINT_VID_MAP)
            .sort(([firstId], [secondId]) => JOINT_VID_MAP[firstId] - JOINT_VID_MAP[secondId]);
          const vids = entries.map(([jointId]) => JOINT_VID_MAP[jointId]);
          // ロボット本体は 0.1 度きざみの角度で関節を受け取る。
          const angles = entries.map(([, degrees]) => Math.round(degrees * 10));
          lines.push(`${INDENT.repeat(level + 2)}{ time: ${numberLiteral(pose.timeMs)}, vids: [${vids.join(', ')}], angles: [${angles.join(', ')}] },`);
        });
        lines.push(`${INDENT.repeat(level + 1)}],`);
        lines.push(`${INDENT.repeat(level)}});`);
        break;
      }
      case 'dance':
        lines.push(`${INDENT.repeat(level)}await robots.dance({ motionId: ${stringLiteral(node.data.motionId)}, name: ${stringLiteral(getDanceLabel(node.data.motionId) ?? '未設定')} });`);
        break;
      case 'qrScan':
        lines.push(`${INDENT.repeat(level)}context.value = await qrScanner.scan();`);
        break;
      case 'tof':
        lines.push(`${INDENT.repeat(level)}context.value = await robots.tof({ mode: 1, area: 0x16 });`);
        break;
      case 'listen':
        lines.push(`${INDENT.repeat(level)}context.value = await listener.listen();`);
        break;
      case 'if':
        return renderIf(node, level, nextOptions);
      case 'mazeGoal':
        return renderMazeGoal(node, level, nextOptions);
      case 'switch':
        return renderSwitch(node, level, nextOptions);
      case 'counter':
        return renderCounter(node, level, nextOptions);
      case 'coinFlip':
        return renderCoinFlip(node, level, nextOptions);
      case 'wait': {
        const seconds = node.data.seconds ?? DEFAULT_WAIT_SECONDS;
        if (!isValidWaitSeconds(seconds)) {
          return indentLines([
            `// Flow warning: ${displayName(node.id)} has an invalid wait setting.`,
            'return;',
          ], level);
        }
        lines.push(`${INDENT.repeat(level)}await wait(${numberLiteral(seconds * 1000)});`);
        break;
      }
      case 'randomChoice': {
        const choices = [
          node.data.choice1 ?? DEFAULT_RANDOM_CHOICES[0],
          node.data.choice2 ?? DEFAULT_RANDOM_CHOICES[1],
          node.data.choice3 ?? DEFAULT_RANDOM_CHOICES[2],
        ];
        if (!choices.every(isValidRandomChoice)) {
          return indentLines([
            `// Flow warning: ${displayName(node.id)} has an invalid choice setting.`,
            'return;',
          ], level);
        }
        lines.push(
          `${INDENT.repeat(level)}context.value = [${choices.map(stringLiteral).join(', ')}][Math.floor(Math.random() * 3)];`,
        );
        break;
      }
      case 'keywordSwitch':
        return renderKeywordSwitch(node, level, nextOptions);
      case 'soundEffect': {
        const sound: unknown = node.data.sound ?? DEFAULT_SOUND_EFFECT;
        if (!isSoundEffect(sound)) {
          return indentLines([
            `// Flow warning: ${displayName(node.id)} has an invalid sound setting.`,
            'return;',
          ], level);
        }
        lines.push(`${INDENT.repeat(level)}await robots.playSound({ sound: ${stringLiteral(sound)} });`);
        break;
      }
      case 'light': {
        const color: unknown = node.data.color ?? DEFAULT_LIGHT_COLOR;
        if (!isLightColor(color)) {
          return indentLines([
            `// Flow warning: ${displayName(node.id)} has an invalid light setting.`,
            'return;',
          ], level);
        }
        const payload = getLightPayload(color);
        lines.push(`${INDENT.repeat(level)}await robots.setLed({ red: ${payload.red}, green: ${payload.green}, blue: ${payload.blue}, mode: ${payload.mode} });`);
        lines.push(`${INDENT.repeat(level)}await wait(${LIGHT_EXECUTION_DURATION_MS});`);
        break;
      }
    }

    const outgoing = sortedOutgoing(node.id);
    if (outgoing.length === 0) {
      lines.push(...indentLines(['// Flow warning: this branch is not connected to another card.'], level));
      return lines;
    }
    lines.push(...renderTargets(outgoing, level, nextOptions));
    return lines;
  }

  const hasReachableLightOn = [...reachable].some((nodeId) => {
    const node = nodeMap.get(nodeId);
    if (node?.type !== 'light') return false;
    const color: unknown = node.data.color ?? DEFAULT_LIGHT_COLOR;
    return isLightColor(color) && getLightPayload(color).mode === 1;
  });
  const lightOffPayload = getLightPayload('off');
  const mainLines: string[] = [
    '// Generated from the current Filo flow.',
    '// This source explains the flow and is not executed directly.',
    '',
    'async function main() {',
    `${INDENT}const context = {};`,
    '',
  ];

  if (!primaryStart) {
    mainLines.push(`${INDENT}// Flow warning: Start is missing.`);
  } else {
    const startOutgoing = sortedOutgoing(primaryStart.id);
    if (hasReachableLightOn) {
      mainLines.push(`${INDENT}try {`);
      mainLines.push(...renderTargets(startOutgoing, 2, {
        path: new Set([primaryStart.id]),
      }));
      mainLines.push(`${INDENT}} finally {`);
      mainLines.push(
        `${INDENT.repeat(2)}await robots.setLed({ red: ${lightOffPayload.red}, green: ${lightOffPayload.green}, blue: ${lightOffPayload.blue}, mode: ${lightOffPayload.mode} });`,
      );
      mainLines.push(`${INDENT}}`);
    } else {
      mainLines.push(...renderTargets(startOutgoing, 1, {
        path: new Set([primaryStart.id]),
      }));
    }
  }
  mainLines.push('}');

  const renderHelperQueue = (queue: HelperDefinition[]): string[] => {
    const rendered: string[] = [];
    let index = 0;
    while (index < queue.length) {
      const helper = queue[index];
      index += 1;
      rendered.push('', `async function ${helper.name}(context) {`);
      rendered.push(...renderFrom(helper.rootId, 1, {
        path: new Set(helper.path),
        allowJoinRoot: helper.kind === 'join',
        stopAtCounterRepeat: helper.stopAtCounterRepeat,
      }));
      rendered.push('}');
    }
    return rendered;
  };

  mainLines.push(...renderHelperQueue(reachableHelpers));

  const unreachable = namingOrder.filter((node) => !reachable.has(node.id));
  if (unreachable.length > 0) {
    collectingUnconnectedHelpers = true;
    const unreachableIds = new Set(unreachable.map((node) => node.id));
    const roots = unreachable.filter((node) =>
      !(inMap.get(node.id) ?? []).some((edge) => unreachableIds.has(edge.source))
    );
    const disconnectedRoots = roots.length > 0 ? roots : unreachable;
    const included = new Set<string>();

    mainLines.push('', '// Unconnected cards');
    for (const root of disconnectedRoots) {
      if (included.has(root.id)) continue;
      const collectConnected = (nodeId: string) => {
        if (included.has(nodeId) || !unreachableIds.has(nodeId)) return;
        included.add(nodeId);
        sortedOutgoing(nodeId).forEach((edge) => collectConnected(edge.target));
      };
      collectConnected(root.id);
      mainLines.push(`// Flow warning: ${displayName(root.id)} is not reachable from ${entryLabel}.`);
      mainLines.push(`async function inspect${displayName(root.id)[0].toUpperCase()}${displayName(root.id).slice(1)}() {`);
      mainLines.push(`${INDENT}const context = {};`);
      mainLines.push(...renderFrom(root.id, 1, { path: new Set(), allowJoinRoot: true }));
      mainLines.push('}', '');
    }
    mainLines.push(...renderHelperQueue(unconnectedHelpers));
  }

  for (const helperLines of functionHelpers.values()) {
    mainLines.push('', ...helperLines);
  }

  while (mainLines.at(-1) === '') mainLines.pop();
  return `${mainLines.join('\n')}\n`;
}
